import { ConflictAction, PartKey, BaseProductApi, ProductIdentifiers, IncludedShelfComponent, raycastCameraTarget, ProductType } from '@inter-ikea-storageone/range';
import { ComponentIds, ConnectionUtils, HierarchyUtils, ReplaceUtils, TransformUtils, ValidationResultFail, Vector } from '@inter-ikea-core/core';
import { PlacementResult } from '@inter-ikea-core/core';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
import CornerSolutionShelfReplacementComponent from './components/CornerSolutionShelfReplacementComponent';
export class PaxProductApi extends BaseProductApi {
    autoFillDoors(targets, entitiesToRemove, products) {
        const isSlidingDoor = products.variants.find(p => p.type === ProductType.PAX_SLIDING_DOOR_REGULAR_ASM ||
            p.type === ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM);
        ConnectionUtils.disconnectAndDisposeEntitiesAndHierarchies(entitiesToRemove);
        const placementResults = [];
        if (isSlidingDoor) {
            const sortedFrames = targets.sort((a, b) => {
                const xSort = TransformUtils.worldPosition(a).X - TransformUtils.worldPosition(b).X;
                return Math.round(xSort) !== 0
                    ? xSort
                    : TransformUtils.worldPosition(a).Z - TransformUtils.worldPosition(b).Z;
            });
            sortedFrames.forEach(frame => {
                const placementResult = this.addToTarget(products.variants[0].coreProductId, frame);
                placementResults.push(placementResult);
            });
            return placementResults;
        }
        targets.forEach((frame) => {
            const size = frame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
            if (!size) {
                return placementResults;
            }
            const variant = products.variants.find(v => v.communicatedDimensions.height === size.height);
            if (variant && !isNaN(variant.dimensions?.width)) {
                placementResults.push(this.addToTarget(variant.coreProductId, frame));
                const spaceLeft = size.width - variant.dimensions.width;
                const allVariants = [...products.variants, ...products.defaults];
                const secondVariant = allVariants.find(v => v.dimensions.width <= spaceLeft && v.communicatedDimensions.height === size.height);
                if (secondVariant) {
                    placementResults.push(this.addToTarget(secondVariant.coreProductId, frame));
                }
            }
        });
        return placementResults;
    }
    add(productId, position) {
        const entity = this.coreServices.productManager.createEntity(productId);
        if (!entity) {
            return new PlacementResult([], [new ValidationResultFail('Failed to create entity', productId)], null);
        }
        const viewer = this.coreServices.coreModules.viewer();
        const worldPosition = position ? viewer?.raycast(position.x, position.y) : null;
        if (worldPosition) {
            return this.placementService.snapToPosition(entity, worldPosition, false);
        }
        else {
            if (!entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
                return this.placementService.snapToScene(entity);
            }
            const raycastHit = raycastCameraTarget(this.ipexViewer);
            if (!raycastHit) {
                return this.placementService.snapToScene(entity);
            }
            const raycastPosition = new Vector(raycastHit?.world_position.x * 1000, raycastHit?.world_position.y * 1000, raycastHit?.world_position.z * 1000);
            return this.placementService.snapToPosition(entity, raycastPosition, true);
        }
    }
    autoFillInnerLights(productId, targets, oldLights) {
        ConnectionUtils.disconnectAndDisposeEntitiesAndHierarchies(oldLights);
        return targets.map((target) => {
            return this.addToTarget(productId, target);
        });
    }
    autoFillOuterLights(productId, frames) {
        const res = frames.map((frame) => {
            const size = frame.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
            const nbrOfOldLights = this.getOuterLightsOnFrame(frame).length;
            let lightStatus = false;
            switch (size?.width) {
                case 750:
                    lightStatus = nbrOfOldLights > 1 ? true : false;
                    return this.addOuterLightsOnFrame(productId, frame, lightStatus, true);
                case 1000:
                    lightStatus = nbrOfOldLights === 1 ? false : true;
                    return this.addOuterLightsOnFrame(productId, frame, lightStatus, true);
                default:
                    return this.addOuterLightsOnFrame(productId, frame, false, true);
            }
        });
        return res?.filter(r => r !== undefined);
    }
    addOuterLightsOnFrame(productId, frame, double, removeOldLights) {
        const oldLights = this.getOuterLightsOnFrame(frame);
        if (removeOldLights) {
            ConnectionUtils.disconnectAndDisposeEntitiesAndHierarchies(oldLights);
        }
        const lightEntity = this.coreServices.productManager.createEntity(productId);
        if (!lightEntity)
            return;
        if (frame.getComponent(ComponentIds.PRODUCT_PART_COMPONENT_ID)?.partKey ===
            PartKey.CORNER_SOLUTION_NORMAL_FRAME) {
            const cornerAssemblyProductRef = HierarchyUtils.parent(frame)?.getComponent(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef;
            const isMirrored = cornerAssemblyProductRef?.includes(ProductIdentifiers.MIRRORED_PRODUCT);
            const connector = isMirrored ? 'light-outer-left' : 'light-outer-right';
            return this.placementService.snapToConnector(lightEntity, 'attachment', frame, connector);
        }
        if (double) {
            // Create a second light if double
            const lightEntity2 = this.coreServices.productManager.createEntity(productId);
            // If we failed to create a light return void
            if (!lightEntity2)
                return;
            this.placementService.snapToConnector(lightEntity, 'attachment', frame, 'light-outer-right');
            return this.placementService.snapToConnector(lightEntity2, 'attachment', frame, 'light-outer-left');
        }
        else {
            return this.placementService.snapToConnector(lightEntity, 'attachment', frame, 'light-outer-center');
        }
    }
    getOuterLightsOnFrame(frame) {
        const children = frame.getComponent(ComponentIds.PARENT_COMPONENT_ID)
            ?.children;
        if (children) {
            return children.filter(child => child.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'outer');
        }
        return [];
    }
    replace(entityId, hostProductId, fastReplace = false, allowedMovementOfChildrenInMilliMeter = Number.MAX_VALUE) {
        const selectedEntity = this.coreServices.sceneManager.findEntity(entityId);
        if (selectedEntity?.hasComponent(CornerSolutionComponent.id)) {
            const includedShelves = [
                ...HierarchyUtils.getDescendantsWithComponent(selectedEntity, IncludedShelfComponent.id),
                ...HierarchyUtils.getDescendantsWithComponent(selectedEntity, CornerSolutionShelfReplacementComponent.id)
            ];
            ConnectionUtils.disconnectAndDisposeEntitiesAndHierarchies(includedShelves);
        }
        const newEntity = this.coreServices.productManager.createEntity(hostProductId);
        if (!selectedEntity || !newEntity) {
            return null;
        }
        //Since we cant add a featureflag atm in this file, this line below is for changeWidthFrames when its time to enable that. So dont remove these comments!
        //this.SwapAndMove(selectedEntity, newEntity);
        const replacePlacementResult = fastReplace
            ? ReplaceUtils.fastReplaceEntity(selectedEntity, newEntity, this.coreServices)
            : ReplaceUtils.replaceEntity(selectedEntity, newEntity, allowedMovementOfChildrenInMilliMeter, this.coreServices);
        if (replacePlacementResult.replacedEntityResult.valid) {
            selectedEntity.dispose();
        }
        return replacePlacementResult;
    }
    performAction(action, params) {
        switch (action) {
            case ConflictAction.ADD_PRODUCT:
                this.add(params.productRef, params.position);
                break;
            case ConflictAction.ADD_PRODUCT_TO_TARGET:
                {
                    const entity = this.coreServices.sceneManager.findEntity(params.parentEntityId);
                    if (!entity)
                        return;
                    const placementResult = this.addToTarget(params.productRef, entity);
                    if (placementResult.valid &&
                        placementResult.placedEntity &&
                        params.entitiesToAddToCollidingEntityProductIds) {
                        this.placementService.dissolveEntity(placementResult.placedEntity);
                        params.entitiesToAddToCollidingEntityProductIds.forEach((productId) => {
                            if (!placementResult.placedEntity)
                                return;
                            this.addToTarget(productId, placementResult.placedEntity);
                        });
                    }
                }
                break;
            case ConflictAction.DELETE_ENTITY: {
                const entity = this.coreServices.sceneManager.findEntity(params.entityId);
                if (entity) {
                    ConnectionUtils.disconnectAndDisposeEntityAndHierarchy(entity);
                }
                break;
            }
            case ConflictAction.ADD_ENTITY_TO_TARGET:
                {
                    const parentEntity = this.coreServices.sceneManager.findEntity(params.parentEntityId);
                    if (!parentEntity)
                        return;
                    const placementResult = this.placementService.snapToTarget(params.entity, parentEntity);
                    if (placementResult.valid && placementResult.placedEntity) {
                        this.placementService.dissolveEntity(placementResult.placedEntity);
                    }
                    if (placementResult.valid &&
                        placementResult.placedEntity &&
                        params.entitiesToAddToCollidingEntityProductIds) {
                        params.entitiesToAddToCollidingEntityProductIds.forEach((productId) => {
                            if (!placementResult.placedEntity)
                                return;
                            this.addToTarget(productId, placementResult.placedEntity);
                        });
                    }
                }
                break;
            default:
                throw new Error('Unkown action');
        }
    }
    /**
     * Used by autofill feature
     * @override
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getTops(products) {
        return [];
    }
    /**
     * Used by autofill feature
     * @override
     */
    getDefaultLeg() {
        return '';
    }
    /**
     * Used by autofill feature
     * @override
     */
    getDefaultHighLegForAutoFill() {
        return '';
    }
}
//# sourceMappingURL=PaxProductApi.js.map